import { useState, useEffect } from 'react';
import WishlistService from '../services/wishlist.service';
import { Link } from 'react-router-dom';
import { Trash2, ShoppingBag } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCart } from '../context/CartContext';

const WishlistPage = () => {
    const [wishlist, setWishlist] = useState(null);
    const [loading, setLoading] = useState(true);
    const { addToCart } = useCart();

    useEffect(() => {
        fetchWishlist();
    }, []);

    const fetchWishlist = async () => {
        try {
            const data = await WishlistService.getWishlist();

            // Handle paginated response or direct array
            const results = data.results || data;

            if (Array.isArray(results) && results.length > 0) {
                // Find default whitelist or first one
                setWishlist(results.find(w => w.is_default) || results[0]);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to load wishlist");
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (productId) => {
        try {
            await WishlistService.removeFromWishlist(wishlist.id, productId);
            fetchWishlist();
            toast.success("Removed from wishlist");
        } catch (err) {
            console.error(err);
            toast.error("Failed to remove item");
        }
    };

    const handleAddToCart = async (product) => {
        // Typically need variant selection. For now, try default.
        // If product has variants, maybe just redirect to details
        // Or if backend add to cart supports product_id... (our context uses variantId)
        // Best UX: Go to product details.
        // Simple UX: Redirect.
        // Let's rely on Link. But user wants "Add to Cart". 
        // We'll redirect to Product Details for variant selection to be safe.
        // Or adding first variant logic?
        // Let's keep it simple: Link to product.
    };

    if (loading) return <div className="p-12 text-center text-zinc-500">Loading wishlist...</div>;

    if (!wishlist || !wishlist.items || wishlist.items.length === 0) {
        return (
            <div className="max-w-4xl mx-auto py-20 px-6 text-center">
                <h1 className="text-3xl font-bold mb-4">Your Wishlist is Empty</h1>
                <p className="text-zinc-500 mb-8">Save items you love to view them here later.</p>
                <Link to="/" className="px-6 py-3 bg-zinc-900 text-white rounded-xl font-bold hover:bg-black transition-colors">
                    Start Shopping
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto py-12 px-6">
            <h1 className="text-3xl font-bold mb-8">My Wishlist ({wishlist.items.length})</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {wishlist.items.map((item) => {
                    const product = item.product_details || item.product; // Handle serializer variations
                    if (!product) return null;

                    const image = product.primary_image || (product.images && product.images[0]?.image_url);

                    return (
                        <div key={item.id} className="group relative bg-white border border-zinc-100 rounded-2xl p-4 hover:shadow-lg transition-all">
                            <button
                                onClick={() => handleRemove(product.id)}
                                className="absolute top-4 right-4 p-2 bg-white/80 rounded-full text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors z-10"
                            >
                                <Trash2 size={18} />
                            </button>

                            <Link to={`/product/${product.id}`} className="block aspect-square bg-zinc-50 rounded-xl overflow-hidden mb-4">
                                {image ? (
                                    <img src={image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-zinc-300 font-bold">No Image</div>
                                )}
                            </Link>

                            <Link to={`/product/${product.id}`}>
                                <h3 className="font-bold text-zinc-900 mb-1 truncate">{product.name}</h3>
                                <p className="text-zinc-500 text-sm mb-3 truncate">{product.description}</p>
                                <div className="flex items-center justify-between">
                                    <span className="font-bold text-lg">GHS {product.base_price}</span>
                                    <div className="p-2 bg-zinc-900 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ShoppingBag size={18} />
                                    </div>
                                </div>
                            </Link>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WishlistPage;
